import type { Periodo } from '@/lib/supabase/tipos-bd'
import { pesos } from '@/shared/utilidades/formato'

/**
 * Cómo se dicen las cifras del tablero: COMPLETAS.
 *
 * Iban abreviadas («$13,8 M») y Johan pidió: «que salgan valores
 * completos». En plata, el redondeo esconde justo lo que se quiere ver; aquí
 * va el valor entero, «$ 13.844.571». Solo los rótulos encima de las barras
 * de la gráfica siguen cortos (`corto`), porque ahí no cabe otra cosa.
 */
export function abreviado(valor: number): string {
  return pesos(valor)
}

/** Para los rótulos de las barras: más corto todavía. */
export function corto(valor: number): string {
  const v = Math.abs(valor)
  if (v === 0) return '0'
  if (v >= 1_000_000)
    return `${(valor / 1_000_000).toLocaleString('es-CO', { maximumFractionDigits: 1 })} M`
  if (v >= 1_000) return `${Math.round(valor / 1_000)} mil`
  return String(Math.round(valor))
}

export type Tendencia = 'sube' | 'baja' | 'igual'

/**
 * EL AUMENTO, EN PORCENTAJE (Johan: «el aumento tiene que ser porcentual,
 * respecto a lo que yo lo quiera ver»).
 *
 * Contra cero no hay porcentaje que decir: de 0 a 3 no es «+∞ %». Ahí se
 * dice «nuevo», que es lo que pasó. De 0 a 0, nada cambió.
 */
export function aumento(actual: number, anterior: number): { texto: string; tendencia: Tendencia } {
  if (anterior === 0 && actual === 0) return { texto: 'igual', tendencia: 'igual' }
  if (anterior === 0) return { texto: 'nuevo', tendencia: 'sube' }
  const pct = Math.round(((actual - anterior) / anterior) * 100)
  if (pct === 0) return { texto: '0 %', tendencia: 'igual' }
  return {
    texto: `${pct > 0 ? '+' : '−'}${Math.abs(pct).toLocaleString('es-CO')} %`,
    tendencia: pct > 0 ? 'sube' : 'baja',
  }
}

const DIAS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb']
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic']
const MESES_LARGOS = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
]

/** La fecha llega como aaaa-mm-dd: se lee en UTC para no correrla un día. */
const leer = (dia: string) => new Date(`${dia}T00:00:00Z`)

/** «lun 29». */
export function diaCorto(dia: string): { nombre: string; numero: string } {
  const d = leer(dia)
  return { nombre: DIAS[d.getUTCDay()], numero: String(d.getUTCDate()) }
}

/** «sept». */
export function mesCorto(dia: string): string {
  return MESES[leer(dia).getUTCMonth()]
}

/**
 * «28 sept – 4 oct», o «21 – 24 sept» si es el mismo mes. Si el rango no es
 * de este año, lleva el año: «1 ene – 1 oct 2025».
 */
export function rango(desde: string, hasta: string): string {
  const año = leer(hasta).getUTCFullYear()
  const texto = rangoSinAño(desde, hasta)
  return año === new Date().getFullYear() ? texto : `${texto} ${año}`
}

function rangoSinAño(desde: string, hasta: string): string {
  const a = leer(desde)
  const b = leer(hasta)
  if (desde === hasta) return `${b.getUTCDate()} ${MESES[b.getUTCMonth()]}`
  const mesA = MESES[a.getUTCMonth()]
  const mesB = MESES[b.getUTCMonth()]
  return mesA === mesB
    ? `${a.getUTCDate()} – ${b.getUTCDate()} ${mesB}`
    : `${a.getUTCDate()} ${mesA} – ${b.getUTCDate()} ${mesB}`
}

/** Cómo se llama el periodo en el selector: «28 sept – 4 oct», «octubre de 2026», «2026». */
export function nombreDelPeriodo(periodo: Periodo, desde: string, hasta: string): string {
  const d = leer(desde)
  if (periodo === 'año') return String(d.getUTCFullYear())
  if (periodo === 'mes') return `${MESES_LARGOS[d.getUTCMonth()]} de ${d.getUTCFullYear()}`
  return rango(desde, hasta)
}

/** «la semana», «el mes», «el año»: para los títulos. */
export const EL_PERIODO: Record<Periodo, string> = {
  semana: 'La semana',
  mes: 'El mes',
  año: 'El año',
}

/** «semana pasada», «mes pasado», «año pasado». */
export const EL_ANTERIOR: Record<Periodo, string> = {
  semana: 'la semana pasada',
  mes: 'el mes pasado',
  año: 'el año pasado',
}

/** «8 semanas», «6 meses», «3 años». */
export function cuantos(periodo: Periodo, n: number): string {
  const nombre = { semana: ['semana', 'semanas'], mes: ['mes', 'meses'], año: ['año', 'años'] }[
    periodo
  ]
  return `${n} ${n === 1 ? nombre[0] : nombre[1]}`
}

/** Un día más o menos, sobre aaaa-mm-dd. */
export function correrDia(dia: string, dias: number): string {
  return new Date(leer(dia).getTime() + dias * 86_400_000).toISOString().slice(0, 10)
}
